import { useState, useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { logAppPromoShown, logAppPromoClicked, logAppPromoDismissed, type PromoPlacement } from '../utils/analytics';

const PROMO_DISMISSED_KEY = 'mhc_app_promo_dismissed_until';
const BANNER_DISMISSED_KEY = 'mhc_app_banner_dismissed_until';
const SNOOZE_DISMISS_MS = 14 * 24 * 60 * 60 * 1000; // 14 days
const SNOOZE_CLICK_MS = 90 * 24 * 60 * 60 * 1000;   // 90 days

export const PLAY_STORE_PACKAGE_ID = 'in.nammamap.mhclive';
export const PLAY_STORE_BASE_URL = `https://play.google.com/store/apps/details?id=${PLAY_STORE_PACKAGE_ID}`;

/**
 * Constructs the Google Play Store link with UTM referrer parameters for store acquisition attribution.
 */
export function getPlayStoreUrl(placement: PromoPlacement = 'dialog'): string {
  const referrer = encodeURIComponent(`utm_source=mhc_court_pulse_web&utm_medium=${placement}&utm_campaign=app_download`);
  return `${PLAY_STORE_BASE_URL}&referrer=${referrer}`;
}

export const PLAY_STORE_URL = getPlayStoreUrl('dialog');

export function usePlayStorePromo() {
  const [isOpen, setIsOpen] = useState(false);
  const isNative = Capacitor.isNativePlatform();
  const hasTriggeredRef = useRef(false);
  const startTimeRef = useRef<number>(Date.now());

  const isDismissed = useCallback(() => {
    try {
      const until = localStorage.getItem(PROMO_DISMISSED_KEY);
      if (!until) return false;
      return Number(until) > Date.now();
    } catch {
      return false;
    }
  }, []);

  const [isBannerVisible, setIsBannerVisible] = useState(() => {
    if (isNative) return false;
    try {
      const until = localStorage.getItem(BANNER_DISMISSED_KEY);
      if (!until) return true;
      return Number(until) <= Date.now();
    } catch {
      return true;
    }
  });

  const triggerPromo = useCallback((triggerType: 'timer' | 'engagement') => {
    if (isNative || hasTriggeredRef.current || isDismissed()) return;
    hasTriggeredRef.current = true;
    setIsOpen(true);
    logAppPromoShown(triggerType);
  }, [isNative, isDismissed]);

  useEffect(() => {
    if (isNative || isDismissed()) return;

    const isAndroid = /Android/i.test(navigator.userAgent);
    const isMobile = isAndroid || /iPhone|iPad|iPod/i.test(navigator.userAgent) || (typeof window !== 'undefined' && window.innerWidth <= 768);

    // Auto-trigger delay: 8s on mobile, 12s on desktop/PC
    const delayMs = isMobile ? 8000 : 12000;
    const minEngagementMs = isMobile ? 5000 : 8000;

    const timer = setTimeout(() => {
      triggerPromo('timer');
    }, delayMs);

    const handleEngagement = () => {
      const elapsed = Date.now() - startTimeRef.current;
      if (elapsed >= minEngagementMs) {
        triggerPromo('engagement');
      }
    };

    window.addEventListener('scroll', handleEngagement, { passive: true, once: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleEngagement);
    };
  }, [isNative, isDismissed, triggerPromo]);

  const dismiss = useCallback((action: 'close_btn' | 'maybe_later' | 'backdrop' = 'close_btn') => {
    try {
      localStorage.setItem(PROMO_DISMISSED_KEY, String(Date.now() + SNOOZE_DISMISS_MS));
    } catch {}
    logAppPromoDismissed(action);
    setIsOpen(false);
  }, []);

  const dismissBanner = useCallback(() => {
    try {
      localStorage.setItem(BANNER_DISMISSED_KEY, String(Date.now() + SNOOZE_DISMISS_MS));
    } catch {}
    logAppPromoDismissed('close_btn');
    setIsBannerVisible(false);
  }, []);

  const openPlayStore = useCallback((source: PromoPlacement = 'dialog') => {
    try {
      localStorage.setItem(PROMO_DISMISSED_KEY, String(Date.now() + SNOOZE_CLICK_MS));
      localStorage.setItem(BANNER_DISMISSED_KEY, String(Date.now() + SNOOZE_CLICK_MS));
    } catch {}
    logAppPromoClicked(source);
    window.open(getPlayStoreUrl(source), '_blank', 'noopener,noreferrer');
    setIsOpen(false);
    setIsBannerVisible(false);
  }, []);

  const openManual = useCallback(() => {
    setIsOpen(true);
    logAppPromoShown('manual_click');
  }, []);

  return {
    isOpen,
    isBannerVisible,
    isNative,
    dismiss,
    dismissBanner,
    openPlayStore,
    openManual,
  };
}
